import axios from 'axios';
import flatpickr from 'flatpickr';
import flatpickrCss from 'flatpickr/dist/flatpickr.min.css?raw';
type FlatpickrInstance = flatpickr.Instance;
import coreStyleText from '@traderverse/core/style.css?raw';
import coreVariablesText from '@traderverse/core/variables.css?raw';
import styleText from './assets/css/style.css?raw';
import traderboardsLogo from './assets/svg/traderboards-logo-mark.svg';
import type { CandlestickChartConfig, WidgetConfig, WidgetTheme } from './types';
import { prepareAssetSearch, prepareRequest } from './auth';

/**
 * Flatpickr walks `document.styleSheets` and reads `.cssRules` on each sheet. On cross-origin
 * stylesheets (e.g. Google Fonts), that access throws SecurityError — see flatpickr
 * `getDocumentStyleSheet` (it checks `if (!sheet.cssRules)` outside try/catch).
 * Prepend a same-origin empty stylesheet so the first entry is always readable.
 */
(function ensureFlatpickrReadableFirstDocumentStylesheet(): void {
  if (typeof document === 'undefined' || !document.head) return;
  if (document.querySelector('style[data-tv-flatpickr-cssrules-fix]')) return;
  const el = document.createElement('style');
  el.setAttribute('data-tv-flatpickr-cssrules-fix', '');
  el.textContent = '/* TraderVerse: first document stylesheet readable for flatpickr */';
  document.head.insertBefore(el, document.head.firstChild);
})();

export const FACTS_ORIGIN = "https://facts.traderverse.io";
export const APIURL = `${FACTS_ORIGIN}/markets/api`;
export const CHART_API_URL = `${FACTS_ORIGIN}/chartapi`;

export type ApiGetOptions = {
  url: string;
  query?: Record<string, string | number>;
  /** Legacy: values are appended as query params (POST bodies converted to GET query). */
  body?: Record<string, unknown> | null;
  headers?: Record<string, string>;
  /** Ignored; `apiGet` always uses GET. Kept so legacy `method: 'POST'` call sites type-check. */
  method?: string;
};

function appendMissingSearchParams(
  url: string,
  query?: Record<string, string | number>,
  body?: Record<string, unknown> | null
): string {
  const u = new URL(url);
  const setIfMissing = (key: string, value: string) => {
    if (!u.searchParams.has(key)) u.searchParams.set(key, value);
  };
  const fromObj = (obj?: Record<string, unknown> | Record<string, string | number> | null) => {
    if (!obj) return;
    for (const [k, v] of Object.entries(obj)) {
      if (v === undefined || v === null) continue;
      if (typeof v === 'object' && !Array.isArray(v)) setIfMissing(k, JSON.stringify(v));
      else if (Array.isArray(v)) setIfMissing(k, v.map(String).join(','));
      else setIfMissing(k, String(v));
    }
  };
  fromObj(query as Record<string, unknown> | undefined);
  fromObj(body);
  return u.toString();
}

export function getNoDataHTML(message = 'No data available'): string {
  return `<div class="noData" data-testing-id="no-data-message"><p>${message}</p></div>`;
}

export function isEmptyWidgetData(data: unknown): boolean {
  if (data == null) return true;
  if (Array.isArray(data)) return data.length === 0;
  if (typeof data !== 'object') return false;
  const obj = data as Record<string, unknown>;
  if (Object.keys(obj).length === 0) return true;
  for (const key of ['data', 'items', 'results', 'rows', 'list', 'values', 'all'] as const) {
    const nested = obj[key];
    if (Array.isArray(nested) && nested.length === 0) return true;
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      const nestedObj = nested as Record<string, unknown>;
      if (Object.keys(nestedObj).length === 0) return true;
      for (const inner of ['data', 'items', 'results', 'rows', 'list', 'values', 'all'] as const) {
        const arr = nestedObj[inner];
        if (Array.isArray(arr) && arr.length === 0) return true;
      }
    }
  }
  return false;
}

/**
 * GET via Axios; merges `query` and `body` into the query string. Returns a `Response` so existing `.ok` / `.json()` call sites keep working.
 * No credential is ever sent from here (SEC-40): Facts routes that need one go through the gateway
 * (see `prepareRequest` in `auth.ts`), with the host's token when there is one.
 */
export async function apiGet(options: ApiGetOptions): Promise<Response> {
  const { url, query, body, headers: customHeaders = {} } = options;
  const prepared = await prepareRequest(appendMissingSearchParams(url, query, body), customHeaders);
  const res = await axios.get<string>(prepared.url, {
    headers: {
      'Content-Type': 'application/json',
      ...prepared.headers,
    },
    validateStatus: () => true,
    responseType: 'text',
    transformResponse: [(data) => data],
  });
  const headerInit: Record<string, string> = {};
  const raw = res.headers;
  if (raw && typeof raw === 'object') {
    for (const key of Object.keys(raw)) {
      const v = raw[key];
      if (v != null) headerInit[key] = Array.isArray(v) ? v.join(', ') : String(v);
    }
  }
  const status = res.status;
  const responseBody =
    status === 101 || status === 204 || status === 205 || status === 304 ? null : (res.data ?? '');
  return new Response(responseBody, {
    status,
    statusText: res.statusText || '',
    headers: new Headers(headerInit),
  });
}

const loadLibraryForImage = async (): Promise<void> => {
  if (!(window as any).domtoimage) {
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/dom-to-image-more@2.8.0/src/dom-to-image-more.min.js';
    document.head.appendChild(script);
    return new Promise((resolve) => script.onload = () => resolve());
  }
};
export const getWidgetDataURI = async (widget: HTMLElement): Promise<string> => {
  await loadLibraryForImage();

  if (!widget || !(window as any).domtoimage) {
    return "";
  }

  // Set crossOrigin attribute for all images inside the widget
  const images = widget.querySelectorAll('img');
  const svgPromises: Promise<void>[] = [];
  images.forEach((img: HTMLImageElement) => {
    // Only set crossOrigin for images from imagedelivery.net
    img.crossOrigin = 'anonymous';
    // Inline SVG images
    if (img.src.includes('.svg')) {
      const promise = fetch(img.src)
        .then(response => response.text())
        .then(svgText => {
          const parser = new DOMParser();
          const svgDoc = parser.parseFromString(svgText, 'image/svg+xml');
          const svgElement = svgDoc.documentElement;

          // Remove width/height attributes to allow scaling
          svgElement.removeAttribute('width');
          svgElement.removeAttribute('height');

          // Set viewBox if missing (assume 56x56 if not present)
          if (!svgElement.hasAttribute('viewBox')) {
            svgElement.setAttribute('viewBox', '0 0 56 56');
          }

          // Set style for scaling inside 26x26 box
          svgElement.style.width = img.clientWidth + 'px';
          svgElement.style.height = img.clientHeight + 'px';
          svgElement.style.display = 'block';

          // Copy style and class from img to svg
          if (img.getAttribute('style')) {
            svgElement.setAttribute('style', img.getAttribute('style')!);
          }
          if (img.getAttribute('class')) {
            svgElement.setAttribute('class', img.getAttribute('class')!);
          }

          img.parentNode?.replaceChild(svgElement, img);
        })
        .catch(() => { /* ignore errors */ });
      svgPromises.push(promise);
    }
  });

  await Promise.all(svgPromises);

  return new Promise((resolve) => {
    (window as any).domtoimage.toPng(widget, { cacheBust: true })
      .then((dataUrl: string) => {
        resolve(dataUrl);
      })
      .catch((err: any) => {
        resolve("");
      });
  });
};

let socketIOLoadPromise: Promise<boolean> | null = null;

export const integrateSocketIOLibrary = () => {
  if ((window as any).io) {
    return Promise.resolve(true);
  }
  if (socketIOLoadPromise) {
    return socketIOLoadPromise;
  }
  socketIOLoadPromise = new Promise<boolean>((resolve) => {
    if ((window as any).io) {
      resolve(true);
      socketIOLoadPromise = null;
    } else if (document.head.querySelector('script[data-socketio-lib]')) {
      const script = document.head.querySelector('script[data-socketio-lib]') as HTMLScriptElement;
      script.onload = () => {
        resolve(true);
        socketIOLoadPromise = null;
      };
    } else {
      const script = document.createElement('script');
      script.src = 'https://cdn.socket.io/4.7.5/socket.io.min.js';
      script.async = true;
      script.setAttribute('data-socketio-lib', 'true');
      document.head.appendChild(script);
      script.onload = () => {
        resolve(true);
        socketIOLoadPromise = null;
      };
    }
  });
  return socketIOLoadPromise;
}

export const getWebsocket = () => {
  return new Promise(async (resolve) => {
    const global = window as any;
    await integrateSocketIOLibrary();
    if (!global.isSocketInitialized && !global.socket) {
      global.socket = (window as any).io("https://websocket.tradersgpt.io", { transports: ['websocket'] });
      global.socketInitialized = true;
      global.socket.on('connect', () => {
        resolve(global.socket);
      });
    } else if (global.socket || global.socketInitialized) {
      global.socketInitialized = true;
      resolve(global.socket);
    }
  });
}

export type SymbolMarket = 'stock' | 'crypto';

export type SymbolOption = {
  stockId: string;
  symbol: string;
  name: string;
  exchange: string;
  type: string;
};

function mapSymbolSearchDocument(doc: any): SymbolOption {
  const tvId = String(doc?.tvId || '').trim();
  const symbol = String(doc?.symbol || '').trim();
  const exchange = String(doc?.exchange || '').trim();
  const assetClass = String(doc?.assetClass || '').toLowerCase();
  const assetType = String(doc?.assetType || '').toLowerCase();
  const isCrypto =
    assetClass === 'crypto' ||
    assetType === 'crypto' ||
    /_crypto$/i.test(tvId) ||
    exchange.toLowerCase() === 'crypto';
  return {
    stockId: tvId || (symbol && exchange ? `${symbol}_${exchange}` : symbol),
    symbol,
    name: String(doc?.name || symbol),
    exchange,
    type: isCrypto ? 'crypto' : 'stock',
  };
}

function fallbackSymbolOptions(keyword: string, market?: SymbolMarket): SymbolOption[] {
  const markets: SymbolMarket[] = market ? [market] : ['stock', 'crypto'];
  const kw = keyword.trim().toLowerCase();
  const list = markets.flatMap((m) => getSymbolOptions(m));
  if (!kw) return list;
  return list.filter((item) =>
    item.symbol.toLowerCase().includes(kw) || item.name.toLowerCase().includes(kw)
  );
}

const symbolSearchCache = new Map<string, SymbolOption[]>();
/** The gateway's anonymous lane caps pages at 30. */
const SYMBOL_SEARCH_MAX_LIMIT = 30;

export type SearchSymbolsOptions = {
  keyword?: string;
  market?: SymbolMarket;
  start?: number;
  limit?: number;
};

export async function searchSymbols(options: SearchSymbolsOptions = {}): Promise<SymbolOption[]> {
  /** The API rejects wildcards in first position: "'*' or '?' not allowed as first character". */
  const keyword = (options.keyword || '').trim().replace(/^[*?]+/, '');
  const market = options.market;
  const start = options.start ?? 0;
  const limit = options.limit ?? 30;
  const cacheKey = `${market || 'all'}|${keyword.toLowerCase()}|${start}|${limit}`;
  const cached = symbolSearchCache.get(cacheKey);
  if (cached) return cached.slice();

  // Shared search gateway (SEC-40: no key in the browser). It adds the prefix `*` itself.
  // Without `q` it returns the highest market-cap symbols, which is what an empty input shows.
  const query = new URLSearchParams({ start: String(start), limit: String(Math.min(limit, SYMBOL_SEARCH_MAX_LIMIT)) });
  const q = keyword.replace(/\*+$/, '');
  if (q) query.set('q', q);
  if (market === 'crypto') query.set('assetClass', 'crypto');
  else if (market === 'stock') query.set('assetClass', 'equity');

  try {
    const request = await prepareAssetSearch(query);
    const res = await axios.get<string>(request.url, {
      headers: request.headers,
      validateStatus: () => true,
      responseType: 'text',
      transformResponse: [(data) => data],
    });
    if (res.status < 200 || res.status >= 300) throw new Error(`Symbol search failed: ${res.status}`);
    const data = JSON.parse(res.data || '{}');
    // Gateway shape: { results: SearchAsset[] } (tvId-first).
    const documents = Array.isArray(data?.results) ? data.results : [];
    let mapped = documents
      .map(mapSymbolSearchDocument)
      .filter((item: SymbolOption) => item.symbol);
    if (market) mapped = mapped.filter((item: SymbolOption) => matchesSymbolMarket(item, market));
    mapped = mapped.slice(0, limit);

    if (mapped.length === 0 && !keyword) {
      return fallbackSymbolOptions(keyword, market);
    }
    symbolSearchCache.set(cacheKey, mapped);
    return mapped.slice();
  } catch {
    return fallbackSymbolOptions(keyword, market);
  }
}

const STOCK_SYMBOL_OPTIONS = [
  { stockId: "MSFT_NASDAQ", symbol: "MSFT", name: "Microsoft Corporation", exchange: "NASDAQ", type: "stock" },
  { stockId: "AAPL_NASDAQ", symbol: "AAPL", name: "Apple Inc.", exchange: "NASDAQ", type: "stock" },
  { stockId: "GOOGL_NASDAQ", symbol: "GOOGL", name: "Alphabet Inc.", exchange: "NASDAQ", type: "stock" },
  { stockId: "AMZN_NASDAQ", symbol: "AMZN", name: "Amazon.com, Inc.", exchange: "NASDAQ", type: "stock" },
  { stockId: "NVDA_NASDAQ", symbol: "NVDA", name: "NVIDIA Corporation", exchange: "NASDAQ", type: "stock" },
  { stockId: "META_NASDAQ", symbol: "META", name: "Meta Platforms, Inc.", exchange: "NASDAQ", type: "stock" },
  { stockId: "TSLA_NASDAQ", symbol: "TSLA", name: "Tesla, Inc.", exchange: "NASDAQ", type: "stock" },
];

const CRYPTO_SYMBOL_OPTIONS = [
  { stockId: "BTC_USD_CRYPTO", symbol: "BTC", name: "Bitcoin", exchange: "CRYPTO", type: "crypto" },
  { stockId: "ETH_USD_CRYPTO", symbol: "ETH", name: "Ethereum", exchange: "CRYPTO", type: "crypto" },
  { stockId: "SOL_USD_CRYPTO", symbol: "SOL", name: "Solana", exchange: "CRYPTO", type: "crypto" },
  { stockId: "XRP_USD_CRYPTO", symbol: "XRP", name: "XRP", exchange: "CRYPTO", type: "crypto" },
  { stockId: "ADA_USD_CRYPTO", symbol: "ADA", name: "Cardano", exchange: "CRYPTO", type: "crypto" },
  { stockId: "DOGE_USD_CRYPTO", symbol: "DOGE", name: "Dogecoin", exchange: "CRYPTO", type: "crypto" },
  { stockId: "BNB_USD_CRYPTO", symbol: "BNB", name: "BNB", exchange: "CRYPTO", type: "crypto" },
];

const matchesSymbolMarket = (item: any, market: SymbolMarket): boolean => {
  const itemType = String(item?.type || '').toLowerCase();
  if (market === 'crypto') {
    return itemType === 'crypto';
  }
  return itemType !== 'crypto';
};

export const getSymbolOptions = (market: SymbolMarket = 'stock') => {
  return market === 'crypto' ? CRYPTO_SYMBOL_OPTIONS : STOCK_SYMBOL_OPTIONS;
}

export const getSymbolDropdown = (onSelect: any, market: SymbolMarket = 'stock') => {
  const container = document.createElement('div');
  container.className = 'itemInner';

  const dropdownContainer = document.createElement('div');
  dropdownContainer.className = 'symbol-dropdown-container';
  dropdownContainer.style.position = 'relative';

  const input = document.createElement('input');
  input.type = 'text';
  input.className = 'symbol-search-input';
  input.placeholder = 'Search symbol...';
  input.setAttribute('aria-label', 'Search symbol');
  input.style.cssText = 'width: 100%; box-sizing: border-box; cursor: text;';

  const dropdown = document.createElement('div');
  dropdown.className = 'symbol-dropdown-list';
  dropdown.style.cssText = '';

  let timeout = null as any;
  let blurCloseTimer: ReturnType<typeof setTimeout> | null = null;
  let requestId = 0;
  async function renderOptions(filter = '') {
    clearTimeout(timeout);
    timeout = setTimeout(async () => {
      const id = ++requestId;
      dropdown.innerHTML = '<div class="symbol-dropdown-status">Searching...</div>';
      const filtered = await searchSymbols({ keyword: filter.trim(), market, limit: 30 });
      if (id !== requestId) return;
      dropdown.innerHTML = '';
      if (filtered.length === 0) {
        dropdown.innerHTML = '<div class="symbol-dropdown-status">No results</div>';
        return;
      }

      let options = filtered;
      const selectedElem = container.parentElement!.querySelector('#selected-symbol') as HTMLElement | null;
      if (selectedElem) {
        const symbolElem: any = selectedElem.querySelector('#selected-symbol-id');
        const nameElem: any = selectedElem.querySelector('#selected-symbol-name');
        const symbolText = symbolElem ? symbolElem.innerText.trim() : '';
        const nameText = nameElem ? nameElem.innerText.trim() : '';

        options = options.filter((item: any) => item.symbol !== symbolText && item.name !== nameText);
        const clone = selectedElem.cloneNode(true) as HTMLElement;
        clone.removeAttribute('id');
        clone.classList.add('selected-symbol-item');
        dropdown.appendChild(clone);
      }

      options.forEach((sym: any) => {
        const div = document.createElement('div');
        div.classList.add("trader-sm-card")
        div.innerHTML = getTraderSMCardHTML(sym);
        div.onmouseenter = () => {
          div.style.background = 'var(--color-hover, color-mix(in srgb, var(--color-text, #fff) 6%, transparent))';
        };
        div.onmouseleave = () => div.style.background = '';
        div.onclick = () => {
          dropdown.style.display = 'none';
          if (blurCloseTimer) {
            clearTimeout(blurCloseTimer);
            blurCloseTimer = null;
          }
          input.value = '';
          input.blur();
          input.dispatchEvent(new Event('change'));
          if (onSelect) onSelect(sym);
        };
        dropdown.appendChild(div);
      });
    }, 500);

  }

  input.addEventListener('focus', () => {
    if (blurCloseTimer) {
      clearTimeout(blurCloseTimer);
      blurCloseTimer = null;
    }
    dropdown.style.display = 'block';
    renderOptions(input.value);
  });
  input.addEventListener('input', () => {
    dropdown.style.display = 'block';
    renderOptions(input.value);
  });
  // Keep focus on the input while picking a row so blur does not fire before click.
  dropdown.addEventListener('mousedown', (e) => {
    e.preventDefault();
  });
  input.addEventListener('blur', () => {
    blurCloseTimer = setTimeout(() => {
      blurCloseTimer = null;
      dropdown.style.display = 'none';
    }, 150);
  });

  dropdownContainer.appendChild(input);
  dropdownContainer.appendChild(dropdown);
  container.appendChild(dropdownContainer);

  // Initial render
  renderOptions();

  return container;
};



function invokeFlatpickrHook(
  hook: flatpickr.Options.Hook | flatpickr.Options.Hook[] | undefined,
  selectedDates: Date[],
  dateStr: string,
  instance: FlatpickrInstance
): void {
  if (!hook) return;
  const hooks = Array.isArray(hook) ? hook : [hook];
  hooks.forEach((fn) => fn(selectedDates, dateStr, instance));
}

/** TraderVerse theme for flatpickr popups, including body portals outside widget CSS variables. */
const FLATPICKR_THEME_CSS = `
.flatpickr-calendar:not(.inline) {
  --tv-fp-bg: var(--color-surface, #0d0a23);
  --tv-fp-heading: var(--color-text, #ffffff);
  --tv-fp-text: color-mix(in srgb, var(--color-text, #ffffff) 70%, transparent);
  --tv-fp-muted: color-mix(in srgb, var(--color-text, #ffffff) 35%, transparent);
  --tv-fp-hover-bg: var(--color-hover, #161031);
  --tv-fp-hover-text: var(--color-text, #ffffff);
  --tv-fp-primary: var(--color-accent, #7b5fff);
  --tv-fp-selected-text: var(--color-accent-fg, #fff);
  background-color: var(--tv-fp-bg) !important;
  border: 1px solid var(--color-border, rgba(255, 255, 255, 0.08)) !important;
  border-radius: 12px !important;
  box-shadow: none !important;
}
.flatpickr-calendar:not(.inline).blackMode,
.flatpickr-calendar:not(.inline).defaultDarkMode {
  --tv-fp-bg: var(--color-surface, #0d0a23);
  --tv-fp-heading: var(--color-text, #ffffff);
  --tv-fp-text: color-mix(in srgb, var(--color-text, #ffffff) 70%, transparent);
  --tv-fp-muted: color-mix(in srgb, var(--color-text, #ffffff) 35%, transparent);
  --tv-fp-hover-bg: var(--color-hover, #161031);
  --tv-fp-hover-text: var(--color-text, #ffffff);
  --tv-fp-primary: var(--color-accent, #7b5fff);
  --tv-fp-selected-text: var(--color-accent-fg, #fff);
}
.flatpickr-calendar:not(.inline).whiteMode {
  --tv-fp-bg: var(--color-surface, #ffffff);
  --tv-fp-heading: var(--color-text, #111827);
  --tv-fp-text: color-mix(in srgb, var(--color-text, #111827) 72%, transparent);
  --tv-fp-muted: color-mix(in srgb, var(--color-text, #111827) 35%, transparent);
  --tv-fp-hover-bg: var(--color-hover, #f3f4f6);
  --tv-fp-hover-text: var(--color-text, #111827);
  --tv-fp-primary: var(--color-accent, #0d9488);
  --tv-fp-selected-text: var(--color-accent-fg, #fff);
}
.flatpickr-calendar:not(.inline).defaultLightMode {
  --tv-fp-bg: var(--color-surface, #ffffff);
  --tv-fp-heading: var(--color-text, #1a1a1a);
  --tv-fp-text: color-mix(in srgb, var(--color-text, #1a1a1a) 72%, transparent);
  --tv-fp-muted: color-mix(in srgb, var(--color-text, #1a1a1a) 35%, transparent);
  --tv-fp-hover-bg: var(--color-hover, #f0eadf);
  --tv-fp-hover-text: var(--color-text, #1a1a1a);
  --tv-fp-primary: var(--color-accent, #6f5cff);
  --tv-fp-selected-text: var(--color-accent-fg, #fff);
}
.flatpickr-calendar:not(.inline).arrowTop:before {
  border-bottom-color: var(--tv-fp-bg);
}
.flatpickr-calendar:not(.inline).arrowTop:after {
  border-bottom-color: var(--tv-fp-bg);
}
.flatpickr-calendar:not(.inline).arrowBottom:before {
  border-top-color: var(--tv-fp-bg);
}
.flatpickr-calendar:not(.inline).arrowBottom:after {
  border-top-color: var(--tv-fp-bg);
}
.flatpickr-calendar:not(.inline) .flatpickr-months .flatpickr-prev-month,
.flatpickr-calendar:not(.inline) .flatpickr-months .flatpickr-next-month,
.flatpickr-calendar:not(.inline) .flatpickr-months .flatpickr-month,
.flatpickr-calendar:not(.inline) .flatpickr-current-month .flatpickr-monthDropdown-months,
.flatpickr-calendar:not(.inline) .flatpickr-current-month input.cur-year {
  color: var(--tv-fp-heading);
  fill: var(--tv-fp-heading);
}
.flatpickr-calendar:not(.inline) .flatpickr-months .flatpickr-prev-month:hover,
.flatpickr-calendar:not(.inline) .flatpickr-months .flatpickr-next-month:hover {
  color: var(--tv-fp-primary);
}
.flatpickr-calendar:not(.inline) .flatpickr-months .flatpickr-prev-month:hover svg,
.flatpickr-calendar:not(.inline) .flatpickr-months .flatpickr-next-month:hover svg {
  fill: var(--tv-fp-primary);
}
.flatpickr-calendar:not(.inline) span.flatpickr-weekday,
.flatpickr-calendar:not(.inline) .flatpickr-day {
  color: var(--tv-fp-text);
}
.flatpickr-calendar:not(.inline) .flatpickr-day.selected,
.flatpickr-calendar:not(.inline) .flatpickr-day.startRange,
.flatpickr-calendar:not(.inline) .flatpickr-day.endRange,
.flatpickr-calendar:not(.inline) .flatpickr-day.selected.inRange,
.flatpickr-calendar:not(.inline) .flatpickr-day.startRange.inRange,
.flatpickr-calendar:not(.inline) .flatpickr-day.endRange.inRange,
.flatpickr-calendar:not(.inline) .flatpickr-day.selected:focus,
.flatpickr-calendar:not(.inline) .flatpickr-day.startRange:focus,
.flatpickr-calendar:not(.inline) .flatpickr-day.endRange:focus,
.flatpickr-calendar:not(.inline) .flatpickr-day.selected:hover,
.flatpickr-calendar:not(.inline) .flatpickr-day.startRange:hover,
.flatpickr-calendar:not(.inline) .flatpickr-day.endRange:hover,
.flatpickr-calendar:not(.inline) .flatpickr-day.selected.prevMonthDay,
.flatpickr-calendar:not(.inline) .flatpickr-day.startRange.prevMonthDay,
.flatpickr-calendar:not(.inline) .flatpickr-day.endRange.prevMonthDay,
.flatpickr-calendar:not(.inline) .flatpickr-day.selected.nextMonthDay,
.flatpickr-calendar:not(.inline) .flatpickr-day.startRange.nextMonthDay,
.flatpickr-calendar:not(.inline) .flatpickr-day.endRange.nextMonthDay {
  background-color: var(--tv-fp-primary);
  border-color: var(--tv-fp-primary);
  color: var(--tv-fp-selected-text);
}
.flatpickr-calendar:not(.inline) .flatpickr-day.inRange,
.flatpickr-calendar:not(.inline) .flatpickr-day.prevMonthDay.inRange,
.flatpickr-calendar:not(.inline) .flatpickr-day.nextMonthDay.inRange,
.flatpickr-calendar:not(.inline) .flatpickr-day.today.inRange,
.flatpickr-calendar:not(.inline) .flatpickr-day.prevMonthDay.today.inRange,
.flatpickr-calendar:not(.inline) .flatpickr-day.nextMonthDay.today.inRange,
.flatpickr-calendar:not(.inline) .flatpickr-day:hover,
.flatpickr-calendar:not(.inline) .flatpickr-day.prevMonthDay:hover,
.flatpickr-calendar:not(.inline) .flatpickr-day.nextMonthDay:hover,
.flatpickr-calendar:not(.inline) .flatpickr-day:focus,
.flatpickr-calendar:not(.inline) .flatpickr-day.prevMonthDay:focus,
.flatpickr-calendar:not(.inline) .flatpickr-day.nextMonthDay:focus {
  background-color: var(--tv-fp-hover-bg);
  border-color: var(--tv-fp-hover-bg);
  color: var(--tv-fp-hover-text);
}
.flatpickr-calendar:not(.inline) .flatpickr-day.inRange {
  -webkit-box-shadow: -5px 0 0 var(--tv-fp-hover-bg), 5px 0 0 var(--tv-fp-hover-bg);
  box-shadow: -5px 0 0 var(--tv-fp-hover-bg), 5px 0 0 var(--tv-fp-hover-bg);
}
.flatpickr-calendar:not(.inline) .flatpickr-day.today {
  border-color: var(--tv-fp-primary);
}
.flatpickr-calendar:not(.inline) .flatpickr-day.today:hover,
.flatpickr-calendar:not(.inline) .flatpickr-day.today:focus {
  background-color: var(--tv-fp-primary);
  border-color: var(--tv-fp-primary);
  color: var(--tv-fp-selected-text);
}
.flatpickr-calendar:not(.inline) .flatpickr-day.flatpickr-disabled,
.flatpickr-calendar:not(.inline) .flatpickr-day.prevMonthDay,
.flatpickr-calendar:not(.inline) .flatpickr-day.nextMonthDay {
  color: var(--tv-fp-muted);
}
`;

const FLATPICKR_SHADOW_OVERLAY_CSS = `
.widgetBox .flatpickr-calendar:not(.inline).open,
.traderverse-widget .flatpickr-calendar:not(.inline).open {
  z-index: 99999;
  pointer-events: auto;
}
${FLATPICKR_THEME_CSS}
`;

const FLATPICKR_DOCUMENT_PORTAL_CSS = `
body > .flatpickr-calendar:not(.inline).open,
body > .flatpickr-calendar:not(.inline).tv-flatpickr-portal {
  z-index: 2147483646;
  pointer-events: auto;
}
${FLATPICKR_THEME_CSS}
`;

/** Base flatpickr CSS on document (popup calendars append to body). */
export function ensureFlatpickrDocumentStyles(): void {
  const existing = document.querySelector('[data-flatpickr-doc-base]') as HTMLStyleElement | null;
  const cssText = flatpickrCss + FLATPICKR_DOCUMENT_PORTAL_CSS;
  if (existing) {
    existing.textContent = cssText;
    return;
  }
  const el = document.createElement('style');
  el.setAttribute('data-flatpickr-doc-base', '');
  el.textContent = cssText;
  document.head.appendChild(el);
}

/** Widget container when a caller explicitly anchors the calendar inside the shadow tree. */
export function getFlatpickrAppendContainer(shadowRoot: ShadowRoot): HTMLElement {
  const container =
    (shadowRoot.querySelector('.widgetBox') as HTMLElement | null) ??
    (shadowRoot.querySelector('.traderverse-widget') as HTMLElement | null);
  if (container && getComputedStyle(container).position === 'static') {
    container.style.position = 'relative';
  }
  return container ?? (shadowRoot as unknown as HTMLElement);
}

function isWidgetFlatpickrContainer(element: HTMLElement): boolean {
  return element.classList.contains('widgetBox') || element.classList.contains('traderverse-widget');
}

function syncFlatpickrTheme(instance: FlatpickrInstance, shadowRoot: ShadowRoot): void {
  const calendar = instance.calendarContainer;
  const widget =
    (shadowRoot.querySelector('.widgetBox') as HTMLElement | null) ??
    (shadowRoot.querySelector('.traderverse-widget') as HTMLElement | null);
  if (!calendar || !widget) return;

  calendar.classList.remove('defaultDarkMode', 'defaultLightMode', 'whiteMode', 'blackMode');
  const themeClass = ['defaultDarkMode', 'defaultLightMode', 'whiteMode', 'blackMode']
    .find((className) => widget.classList.contains(className));
  calendar.classList.add(themeClass ?? 'defaultDarkMode');
}

/** Align popup to the input when appendTo is a positioned widget shell (not document.body). */
export function alignFlatpickrCalendar(
  instance: FlatpickrInstance,
  input: HTMLInputElement,
  container: HTMLElement
): void {
  const calendar = instance.calendarContainer;
  if (!calendar) return;
  const inputRect = input.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  calendar.style.position = 'absolute';
  calendar.style.top = `${inputRect.bottom - containerRect.top + container.scrollTop + 2}px`;
  calendar.style.left = `${inputRect.left - containerRect.left + container.scrollLeft}px`;
  calendar.style.right = 'auto';
  calendar.style.bottom = 'auto';
}

/** Stop drag/layout parents from stealing mousedown before flatpickr handles day clicks. */
function bindFlatpickrCalendarEventIsolation(instance: FlatpickrInstance): void {
  const calendar = instance.calendarContainer;
  if (!calendar || (calendar as HTMLElement & { __tvFpIsolation?: boolean }).__tvFpIsolation) return;
  (calendar as HTMLElement & { __tvFpIsolation?: boolean }).__tvFpIsolation = true;
  calendar.classList.add('tv-flatpickr-portal');
  const stop = (e: Event) => e.stopPropagation();
  calendar.addEventListener('mousedown', stop);
  calendar.addEventListener('pointerdown', stop);
  calendar.addEventListener('click', stop);
  calendar.addEventListener('touchstart', stop);
}

/**
 * Initialize flatpickr for Shadow DOM widgets.
 * Popups default to `document.body` so viewport positioning and click targets stay correct.
 */
export function initFlatpickrInShadow(
  input: HTMLElement,
  shadowRoot: ShadowRoot,
  options: flatpickr.Options.Options = {}
): FlatpickrInstance {
  ensureFlatpickrDocumentStyles();

  const htmlInput = input as HTMLInputElement;
  const appendTarget = options.appendTo ?? document.body;
  const useWidgetPositioning =
    appendTarget !== document.body &&
    appendTarget instanceof HTMLElement &&
    isWidgetFlatpickrContainer(appendTarget);

  const userOnReady = options.onReady;
  const userOnOpen = options.onOpen;
  const userPosition = options.position;
  const { appendTo: _appendTo, position: _position, onReady: _onReady, onOpen: _onOpen, ...rest } = options;

  const alignIfNeeded = (instance: FlatpickrInstance) => {
    if (useWidgetPositioning && appendTarget instanceof HTMLElement) {
      alignFlatpickrCalendar(instance, htmlInput, appendTarget);
    }
  };

  const instance = flatpickr(input, {
    ...rest,
    disableMobile: rest.disableMobile ?? true,
    appendTo: appendTarget,
    ...(useWidgetPositioning || typeof userPosition === 'function'
      ? {
        position: (inst, ...args) => {
          alignIfNeeded(inst);
          if (typeof userPosition === 'function') {
            userPosition(inst, ...args);
          }
        },
      }
      : {}),
    onReady: (selectedDates, dateStr, inst) => {
      bindFlatpickrCalendarEventIsolation(inst);
      syncFlatpickrTheme(inst, shadowRoot);
      alignIfNeeded(inst);
      invokeFlatpickrHook(userOnReady, selectedDates, dateStr, inst);
    },
    onOpen: (selectedDates, dateStr, inst) => {
      bindFlatpickrCalendarEventIsolation(inst);
      syncFlatpickrTheme(inst, shadowRoot);
      alignIfNeeded(inst);
      invokeFlatpickrHook(userOnOpen, selectedDates, dateStr, inst);
    },
  });

  return instance;
}

/** Flatpickr base styles for Shadow DOM widgets (`static` mode renders inside the shadow tree; document-level CSS does not apply). */
export function appendFlatpickrBaseStyles(shadowRoot: ShadowRoot): void {
  const existing = shadowRoot.querySelector('[data-flatpickr-base]') as HTMLStyleElement | null;
  const cssText = flatpickrCss + FLATPICKR_SHADOW_OVERLAY_CSS;
  if (existing) {
    existing.textContent = cssText;
    return;
  }
  const el = document.createElement('style');
  el.setAttribute('data-flatpickr-base', '');
  el.textContent = cssText;
  shadowRoot.appendChild(el);
}

let widgetHeaderAppendHookInstalled = false;

const ensureWidgetHeaderAppendHook = (): void => {
  if (widgetHeaderAppendHookInstalled || typeof ShadowRoot === 'undefined') return;
  widgetHeaderAppendHookInstalled = true;

  const originalAppendChild = ShadowRoot.prototype.appendChild;
  ShadowRoot.prototype.appendChild = function <T extends Node>(node: T): T {
    const appended = originalAppendChild.call(this, node) as T;
    if (node instanceof HTMLElement && node.hasAttribute('data-tv-widget-styles')) {
      normalizeWidgetHeaderControls(this);
    }
    return appended;
  };
};

export const getCSSStyles = async () => {
  ensureWidgetHeaderAppendHook();
  const styleEl = document.createElement('style');
  styleEl.setAttribute('data-tv-widget-styles', '');
  const resolvedCoreStyleText = coreStyleText.replace(
    /@import\s+(?:["']\.\/variables\.css["']|["']tailwindcss["']);\s*/g,
    '',
  );
  styleEl.textContent = `${coreVariablesText}\n${resolvedCoreStyleText}\n${styleText}`;
  setTimeout(() => {
    const root = styleEl.getRootNode();
    if (root instanceof ShadowRoot) {
      normalizeWidgetHeaderControls(root);
    }
  }, 0);
  return styleEl;
}

const getCssVariable = (
  element: HTMLElement | null | undefined,
  propertyName: string,
  fallback: string
): string => {
  if (!element) return fallback;
  const value = getComputedStyle(element).getPropertyValue(propertyName).trim();
  return value || fallback;
};

const isWhiteWidgetTheme = (widgetBox: HTMLElement | null | undefined): boolean =>
  widgetBox?.getAttribute('data-theme') === 'white' || widgetBox?.classList.contains('whiteMode') === true;

const getTooltipThemeValues = (widgetBox: HTMLElement | null | undefined) => {
  const lightTheme = isWhiteWidgetTheme(widgetBox);
  const backgroundColor = getCssVariable(widgetBox, '--lightest-bg-color', lightTheme ? '#ffffff' : '#1a152c');
  const borderColor = getCssVariable(widgetBox, '--border-color-01', lightTheme ? '#E1E3CE' : '#302C57');
  const textColor = getCssVariable(widgetBox, '--text-white', lightTheme ? '#444444' : '#ffffff');
  const fontFamily = getCssVariable(widgetBox, '--font-f-inter', 'Inter, sans-serif');
  const fontSize = getCssVariable(widgetBox, '--font-size-sm', '12px');

  return { backgroundColor, borderColor, textColor, fontFamily, fontSize };
};

export const getEChartsTooltipTheme = (widgetBox: HTMLElement | null | undefined) => {
  const { backgroundColor, borderColor, textColor, fontFamily, fontSize } = getTooltipThemeValues(widgetBox);

  return {
    backgroundColor,
    borderColor,
    borderWidth: 1,
    textStyle: {
      color: textColor,
      fontFamily,
      fontSize
    },
    extraCssText: [
      'border-radius: 8px',
      'box-shadow: 0 8px 24px rgba(0,0,0,0.18)',
      `background-color: ${backgroundColor} !important`,
      `border-color: ${borderColor} !important`,
      `color: ${textColor} !important`
    ].join(';')
  };
};

export const getHighchartsTooltipTheme = (widgetBox: HTMLElement | null | undefined) => {
  const { backgroundColor, borderColor, textColor, fontFamily, fontSize } = getTooltipThemeValues(widgetBox);

  return {
    backgroundColor,
    borderColor,
    borderRadius: 8,
    borderWidth: 1,
    shadow: false,
    style: {
      color: textColor,
      fontFamily,
      fontSize
    }
  };
};

const FILTER_TOGGLE_SVG = '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M12.1794 0.950592H1.72077C1.57285 0.950469 1.42802 0.991908 1.30363 1.06995C1.17925 1.14799 1.08058 1.25932 1.01944 1.39061C0.958302 1.5219 0.937286 1.66759 0.958909 1.81023C0.980533 1.95286 1.04388 2.0864 1.14136 2.19484L5.22108 6.73834C5.34378 6.8752 5.41132 7.05084 5.41114 7.23259V10.8881C5.41114 10.9463 5.42504 11.0037 5.45175 11.0558C5.47846 11.1079 5.51725 11.1532 5.56503 11.1881L7.87343 12.8756C7.93059 12.9174 7.99856 12.9428 8.06972 12.9491C8.14088 12.9553 8.21242 12.9421 8.27633 12.911C8.34023 12.8799 8.39398 12.832 8.43154 12.7727C8.46911 12.7135 8.489 12.6452 8.489 12.5756V7.23259C8.48882 7.05084 8.55636 6.8752 8.67906 6.73834L12.7588 2.19409C13.1943 1.70959 12.8403 0.950592 12.1794 0.950592Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/> </svg>';

const isFilterToggleButton = (button: HTMLButtonElement): boolean => {
  if (button.closest('.filterChip, .pp2-filters-menu, [role="menu"]')) return false;

  const attributeText = [
    button.id,
    button.getAttribute('class'),
    button.getAttribute('data-testing-id'),
    button.getAttribute('aria-label'),
    button.textContent,
  ].join(' ').toLowerCase();

  return attributeText.includes('filters-toggle')
    || attributeText.includes('filterstoggle')
    || attributeText.includes('filter-toggle')
    || attributeText.includes('filters');
};

const getFilterToggleIn = (element: ParentNode): HTMLButtonElement | null => {
  return (Array.from(element.querySelectorAll('button')) as HTMLButtonElement[])
    .find((button) => isFilterToggleButton(button)) ?? null;
};

const normalizeFilterToggleButton = (button: HTMLButtonElement): void => {
  button.classList.add('filters-toggle');
  if (!button.getAttribute('type')) button.setAttribute('type', 'button');
  button.setAttribute('aria-label', 'Filters');

  Array.from(button.childNodes).forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) {
      node.remove();
    }
  });

  if (!button.querySelector('svg')) {
    button.innerHTML = FILTER_TOGGLE_SVG;
  }
};

const FILTERS_OUTSIDE_WIRED = 'data-filters-outside-wired';

/**
 * Close `.filtersBox` when the user taps outside the panel (and outside the
 * toggle). Existing per-widget handlers often only close when the click is
 * outside the entire widget host — so chart taps never dismissed filters.
 */
const wireFiltersOutsideClick = (
  filtersBox: HTMLElement | null,
  filtersToggle: HTMLButtonElement | null,
  shadowRoot?: ShadowRoot | HTMLElement,
): void => {
  if (!filtersBox || filtersBox.getAttribute(FILTERS_OUTSIDE_WIRED) === '1') return;
  filtersBox.setAttribute(FILTERS_OUTSIDE_WIRED, '1');

  filtersBox.addEventListener('click', (ev: MouseEvent) => ev.stopPropagation());
  filtersToggle?.addEventListener('click', (ev: MouseEvent) => ev.stopPropagation());

  const rootActive = (): Element | null => {
    if (shadowRoot && 'activeElement' in shadowRoot) {
      return (shadowRoot as ShadowRoot).activeElement;
    }
    return document.activeElement;
  };

  const onOutside = (ev: Event) => {
    if (filtersBox.classList.contains('hidden')) return;
    const path =
      typeof (ev as PointerEvent).composedPath === 'function'
        ? ((ev as PointerEvent).composedPath() as EventTarget[])
        : [];

    // Native <select> / <option> — OS picker is often absent from composedPath.
    if (
      path.some(
        (node) =>
          node instanceof HTMLSelectElement ||
          node instanceof HTMLOptionElement ||
          (node instanceof Element && Boolean(node.closest?.('select, option'))),
      )
    ) {
      return;
    }

    if (path.includes(filtersBox)) return;
    if (filtersToggle && path.includes(filtersToggle)) return;

    window.setTimeout(() => {
      if (filtersBox.classList.contains('hidden')) return;
      const active = rootActive();
      if (
        active &&
        filtersBox.contains(active) &&
        (active instanceof HTMLSelectElement ||
          active instanceof HTMLInputElement ||
          active instanceof HTMLTextAreaElement ||
          active instanceof HTMLButtonElement)
      ) {
        return;
      }
      filtersBox.classList.add('hidden');
    }, 0);
  };

  document.addEventListener('pointerdown', onOutside, true);
};

const normalizeWidgetHeaderControls = (shadowRoot: ShadowRoot | HTMLElement): void => {
  const widget = shadowRoot.querySelector('.widgetBox') as HTMLElement | null;
  const headTop = widget?.querySelector('.headTop') as HTMLElement | null;
  const title = headTop?.querySelector('.title');
  if (!widget || !headTop || !title) return;
  const symbolMarket: SymbolMarket = title.classList.contains('crypto') ? 'crypto' : 'stock';

  let actionCol = headTop.querySelector('.actionCol') as HTMLElement | null;
  if (!actionCol) {
    actionCol = document.createElement('div');
    actionCol.className = 'actionCol';
    headTop.appendChild(actionCol);
  }

  const headerSymbolItem = actionCol.querySelector('.item.dropStyle01');
  const bodySymbolCardItem = widget.querySelector('.inBody .optionsRow .item.dropStyle01') as HTMLElement | null;
  const nativeSymbolSelect: any = widget.querySelector('.inBody .optionsRow .symbol-dropdown') as HTMLSelectElement | null;
  const nativeSymbolItem = nativeSymbolSelect?.closest('.item') as HTMLElement | null;

  if (!headerSymbolItem) {
    if (bodySymbolCardItem) {
      actionCol.prepend(bodySymbolCardItem);
    } else if (nativeSymbolItem) {
      const selectedOption = nativeSymbolSelect?.selectedOptions?.[0] ?? null;
      const rawSymbol = selectedOption?.textContent?.trim() || nativeSymbolSelect?.value || 'AAPL';
      const symbol = rawSymbol.includes('_') ? rawSymbol.split('_')[0] : rawSymbol;
      const stock = { symbol, type: symbolMarket, name: '' };
      const symbolItem = document.createElement('div');
      symbolItem.className = 'item dropStyle01';
      symbolItem.innerHTML = `
        <div class="trader-sm-card" id="selected-symbol">
          ${getTraderSMCardHTML(stock)}
        </div>
        <span id="symbol-dropdown-placeholder"></span>
      `;

      const dropdown = getSymbolDropdown((selectedStock: any) => {
        const nextValue = Array.from(nativeSymbolSelect.options).some((option: any) => option.value === selectedStock.stockId)
          ? selectedStock.stockId
          : selectedStock.symbol;
        nativeSymbolSelect.value = nextValue;
        const selectedSymbol = symbolItem.querySelector('#selected-symbol') as HTMLElement | null;
        if (selectedSymbol) {
          selectedSymbol.innerHTML = getTraderSMCardHTML(selectedStock);
        }
        nativeSymbolSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }, symbolMarket);
      const placeholder = symbolItem.querySelector('#symbol-dropdown-placeholder');
      if (placeholder && dropdown) placeholder.replaceWith(dropdown);

      nativeSymbolItem.hidden = true;
      actionCol.prepend(symbolItem);
    }
  }

  const inBody = widget.querySelector('.inBody') as HTMLElement | null;
  let filtersBox = widget.querySelector('.filtersBox') as HTMLElement | null;
  const filterControlItems = Array.from(widget.querySelectorAll('.inBody .optionsRow .item')).filter((item) => {
    if (!(item instanceof HTMLElement) || item.hidden || item.classList.contains('dropStyle01')) return false;
    if (item.querySelector('.symbol-dropdown') || getFilterToggleIn(item)) return false;
    return Boolean(item.querySelector('select, input, textarea'));
  }) as HTMLElement[];

  if (filterControlItems.length > 0 && inBody) {
    if (!filtersBox) {
      filtersBox = document.createElement('div');
      filtersBox.className = 'filtersBox sideBar hidden';
      inBody.insertBefore(filtersBox, inBody.firstElementChild);
    }
    filterControlItems.forEach((item) => filtersBox!.appendChild(item));
  }

  const bodyFiltersToggle = inBody ? getFilterToggleIn(inBody) : null;
  let filtersToggle = (actionCol.querySelector('.filters-toggle') as HTMLButtonElement | null)
    ?? (widget.querySelector('.filters-toggle') as HTMLButtonElement | null)
    ?? bodyFiltersToggle;
  if (!filtersToggle && filtersBox && filtersBox.querySelector('.item')) {
    const filterItem = document.createElement('div');
    filterItem.className = 'item';
    filterItem.innerHTML = `
      <button class="filters-toggle" type="button" aria-label="Filters">
        ${FILTER_TOGGLE_SVG}
      </button>
    `;
    filtersToggle = filterItem.querySelector('.filters-toggle') as HTMLButtonElement;
    filtersToggle.addEventListener('click', () => filtersBox?.classList.toggle('hidden'));
    actionCol.appendChild(filterItem);
  }

  if (filtersToggle) {
    normalizeFilterToggleButton(filtersToggle);
    const filterItem = (filtersToggle.closest('.item') as HTMLElement | null) ?? filtersToggle;
    if (!actionCol.contains(filterItem)) {
      actionCol.appendChild(filterItem);
    }
  }

  if (filtersBox && inBody) {
    filtersBox.classList.add('sideBar');
    if (filtersBox.parentElement !== inBody) {
      inBody.insertBefore(filtersBox, inBody.firstElementChild);
    }
  }

  wireFiltersOutsideClick(filtersBox, filtersToggle, shadowRoot);

  const directEllipsisItems = Array.from(actionCol.children).filter((child) =>
    child instanceof HTMLElement && child.matches('i.pi-ellipsis-v, .pi.pi-ellipsis-v')
  );
  if (actionCol.children.length > directEllipsisItems.length) {
    directEllipsisItems.forEach((item) => item.remove());
  }

  widget.querySelectorAll('.inBody .optionsRow').forEach((row) => {
    const meaningfulChildren = Array.from(row.children).filter((child) => {
      if (!(child instanceof HTMLElement)) return false;
      if (child.hidden) return false;
      if (child.tagName.toLowerCase() === 'span' && !child.textContent?.trim()) return false;
      return true;
    });
    if (meaningfulChildren.length === 0) {
      row.remove();
    }
  });
};

export const cancelSelectEvent = (shadowRoot: any) => {
  normalizeWidgetHeaderControls(shadowRoot);
  applyWidgetSelectColorScheme(shadowRoot);

  const selects = shadowRoot.querySelectorAll('select');
  selects.forEach((select: HTMLSelectElement) => {
    select.addEventListener('mousedown', (e: any) => {
      e.stopPropagation(); // Prevent drag start
    });
    select.addEventListener('touchstart', (e: any) => {
      e.stopPropagation(); // For mobile
    });
  });

  const inputs = shadowRoot.querySelectorAll('input');
  inputs.forEach((input: HTMLInputElement) => {
    input.addEventListener('mousedown', (e: any) => {
      e.stopPropagation();
    });
    input.addEventListener('touchstart', (e: any) => {
      e.stopPropagation();
    });
  });
}

export const normalizeWidgetTheme = (theme?: string | null): WidgetTheme => {
  switch (theme) {
    case 'white':
    case 'black':
    case 'default-dark':
    case 'default-lite':
      return theme;
    case 'default-light':
      return 'default-lite';
    case 'dark':
      return 'black';
    case 'light':
      return 'white';
    case 'default':
    default:
      return 'default-dark';
  }
};

export const getWidgetThemeClass = (theme?: string | null): string => {
  switch (normalizeWidgetTheme(theme)) {
    case 'white':
      return 'whiteMode';
    case 'black':
      return 'blackMode';
    case 'default-lite':
      return 'defaultLightMode';
    case 'default-dark':
    default:
      return 'defaultDarkMode';
  }
};

export const getWidgetColorScheme = (theme?: string | null): 'dark' | 'light' => {
  switch (normalizeWidgetTheme(theme)) {
    case 'white':
    case 'default-lite':
      return 'light';
    case 'black':
    case 'default-dark':
    default:
      return 'dark';
  }
};

/** Keep native <select>/<option> popups aligned with the active widget theme. */
export const applyWidgetSelectColorScheme = (
  root: ShadowRoot | ParentNode,
  theme?: string | null
): void => {
  const widget =
    (root.querySelector('.widgetBox') as HTMLElement | null) ||
    (root.querySelector('.traderverse-widget') as HTMLElement | null);
  if (!widget) return;

  const colorScheme = getWidgetColorScheme(
    theme ?? widget.getAttribute('data-theme') ?? undefined
  );
  widget.style.colorScheme = colorScheme;
  root.querySelectorAll('select').forEach((el) => {
    (el as HTMLSelectElement).style.colorScheme = colorScheme;
  });
};

export const toggleWidgetTheme = (
  shadowRoot: ShadowRoot,
  theme: string
) => {
  const normalizedTheme = normalizeWidgetTheme(theme);
  const widget =
    shadowRoot.querySelector('.widgetBox') ||
    shadowRoot.querySelector('.traderverse-widget');

  if (!widget) return normalizedTheme;

  widget.classList.remove('defaultDarkMode', 'defaultLightMode', 'whiteMode', 'blackMode');
  widget.setAttribute('data-theme', normalizedTheme);
  widget.classList.add(getWidgetThemeClass(normalizedTheme));
  applyWidgetSelectColorScheme(shadowRoot, normalizedTheme);
  return normalizedTheme;
};



export const getTraderSMCardHTML = (stock: { symbol: string; name: string; type?: string }) => {
  if (!stock.name) {
    const stockData = [...getSymbolOptions('stock'), ...getSymbolOptions('crypto')].find(item => item.symbol === stock.symbol);
    stock.name = stockData ? stockData.name : 'Unknown';
  }
  const symbolImage =
    stock.type === 'crypto'
      ? `https://media.traderverse.io/assets/crypto/${stock.symbol.toUpperCase()}_CRYPTO.svg`
      : `https://media.traderverse.io/assets/equity/${stock.symbol.toUpperCase()}_NASDAQ.svg`;
 
  return `
     <figure>
        <img src="${symbolImage}" onerror="this.onerror=null;this.src='${traderboardsLogo}';" alt="${stock.symbol}">
      </figure>
      <div class="info">
        <h3>
          <span id="selected-symbol-id" class="c-pointer">${stock.symbol}</span>
          <small id="selected-symbol-name">${stock.name}</small>
        </h3>
      </div>
      `;
}

export function updateSelectedSymbolInDropdown(
  container: HTMLElement | ShadowRoot,
  stockId: string,
  stock: { symbol: string; name: string; type?: string }
): void {
  const selectedStock = container.querySelector('#selected-symbol') as HTMLDivElement | null;
  if (selectedStock) {
    selectedStock.innerHTML = getTraderSMCardHTML(stock);
  }
  const searchInput = container.querySelector('.symbol-search-input') as HTMLInputElement | null;
  if (searchInput) {
    searchInput.value = '';
    searchInput.blur();
  }
}


export const getStartDateFromPeriod = (period: string) => {
  if (!period || typeof period !== 'string') {
    period = '5D';
  }

  let startDate: Date;
  const nowUTC = new Date();

  switch ((period || '').toUpperCase().replace(/\s/g, '')) {
    case '1D':
    case '1DAY':
      startDate = new Date(Date.UTC(
        nowUTC.getUTCFullYear(),
        nowUTC.getUTCMonth(),
        nowUTC.getUTCDate(),
        0, 0, 0
      ));
      break;
    case '5D':
    case '5DAYS':
      startDate = new Date(Date.UTC(
        nowUTC.getUTCFullYear(),
        nowUTC.getUTCMonth(),
        nowUTC.getUTCDate() - 4,
        0, 0, 0
      ));
      break;
    case '1M':
    case '1MONTH':
      startDate = new Date(Date.UTC(
        nowUTC.getUTCFullYear(),
        nowUTC.getUTCMonth() - 1,
        nowUTC.getUTCDate(),
        0, 0, 0
      ));
      break;
    case '3M':
    case '3MONTHS':
      startDate = new Date(Date.UTC(
        nowUTC.getUTCFullYear(),
        nowUTC.getUTCMonth() - 3,
        nowUTC.getUTCDate(),
        0, 0, 0
      ));
      break;
    case '6M':
    case '6MONTHS':
      startDate = new Date(Date.UTC(
        nowUTC.getUTCFullYear(),
        nowUTC.getUTCMonth() - 6,
        nowUTC.getUTCDate(),
        0, 0, 0
      ));
      break;
    case '1Y':
    case '1YEAR':
      startDate = new Date(Date.UTC(
        nowUTC.getUTCFullYear() - 1,
        nowUTC.getUTCMonth(),
        nowUTC.getUTCDate(),
        0, 0, 0
      ));
      break;
    case '5Y':
    case '5YEARS':
      startDate = new Date(Date.UTC(
        nowUTC.getUTCFullYear() - 5,
        nowUTC.getUTCMonth(),
        nowUTC.getUTCDate(),
        0, 0, 0
      ));
      break;
    default:
      // Default to 5 days if period is not recognized
      startDate = new Date(Date.UTC(
        nowUTC.getUTCFullYear(),
        nowUTC.getUTCMonth(),
        nowUTC.getUTCDate() - 4,
        0, 0, 0
      ));
      break;
  }
  return startDate;
}

export const getNumAndUnitFromInterval = (selected: string) => {
  let num = 1;
  let unit = "m";

  const match = selected.match(/^(\d+)([mhdWM])$/i);
  if (match) {
    num = parseInt(match[1]);
    unit = match[2];
  }

  return { num, unit };
}

export const getIntervalAndSubpointsFromPeriod = (selected: string) => {
  let interval = "minute";
  let subPoints = 1;

  const { num, unit } = getNumAndUnitFromInterval(selected);

  if (unit === "m") {
    interval = "minute";
    subPoints = num;
  } else if (unit === "h") {
    interval = "hour";
    subPoints = num;
  } else if (unit === "D") {
    interval = "daily";
    subPoints = num;
  } else if (unit === "W") {
    interval = "weekly";
    subPoints = num;
  } else if (unit === "M") {
    interval = "monthly";
    subPoints = num;
  }

  return { interval, subPoints };
}

export const getAlignedTimestampFromInterval = (timestamp: number, interval: string) => {
  let periodMs = 60000;
  if (!interval || typeof interval !== 'string') {
    interval = '1m';
  }
  const { unit, num } = getNumAndUnitFromInterval(interval);
  if (unit === "M") {
    periodMs = num * 30 * 24 * 60 * 60 * 1000;
  } else if (unit === "W") {
    periodMs = num * 7 * 24 * 60 * 60 * 1000;
  } else if (unit === "D") {
    periodMs = num * 24 * 60 * 60 * 1000;
  } else if (unit === "h") {
    periodMs = num * 60 * 60 * 1000;
  } else {
    periodMs = num * 60 * 1000;
  }
  const alignedTime = Math.ceil(timestamp / periodMs) * periodMs;
  const alignedFloorTime = Math.floor(timestamp / periodMs) * periodMs;
  const newTime = new Date(alignedFloorTime).getTime() === new Date(timestamp).setSeconds(0, 0) ? new Date(alignedFloorTime) : new Date(alignedTime);
  if (unit === "M" || unit === "W" || unit === "D") {
    newTime.setHours(0, 0, 0, 0); // Align to start of day for day/week/month intervals
  } else if (unit === "h") {
    newTime.setMinutes(0, 0, 0); // Ensure seconds and milliseconds are 0
  } else {
    newTime.setSeconds(0, 0);
  }
  return newTime;
}

export const defaultIntervalOptions = {
  "1D": ["1m", "5m", "15m", "30m", "1h", "2h", "4h"],
  "5D": ["1m", "5m", "15m", "30m", "1h", "2h", "4h"],
  "1M": ["30m", "1h", "2h", "4h", "1D"],
  "3M": ["1h", "2h", "4h", "1D", "1W"],
  "6M": ["2h", "4h", "1D", "1W"],
  "1Y": ["1D", "1W", "1M"],
  "5Y": ["1D", "1W", "1M"],
};

export const periodDefaultValue = {
  "1D": "1m",
  "5D": "15m",
  "1M": "1h",
  "3M": "1D",
  "6M": "1D",
  "1Y": "1D",
  "5Y": "1W",
};

export const changeSymbolForWidget = (
  shadowRoot: any,
  config: any,
  stock: any,
  stockId: string,
  actionFn: (stockId: string, stock?: any) => void,
  emit: (type: any, data: any) => void,
) => {
  if (!stock) {
    if (typeof stockId === 'string') {
      const parts = stockId.split('_');
      stock = { symbol: parts[0], name: parts[0], type: 'stock' };
    } else {
      stock = JSON.parse(JSON.stringify(stockId));
      stockId = stock.stockId;
    }
  }
  config.onSymbolChange?.(stock);
  emit('symbolChange', { stockId, stock });
  config.symbol = stockId;
  config.stock = stock;
  actionFn(stockId, stock);
  updateSelectedSymbolInDropdown(shadowRoot, stockId, stock);
}

export function isHeaderPinned(config: Pick<WidgetConfig, 'headerPinned'>): boolean {
  return config.headerPinned !== false;
}

export function getHeaderPinStateClass(config: Pick<WidgetConfig, 'headerPinned'>): string {
  return isHeaderPinned(config) ? 'header-pinned' : 'header-unpinned';
}

function getPinIconSvg(): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640"><path d="M73 39.1C63.6 29.7 48.4 29.7 39.1 39.1C29.8 48.5 29.7 63.7 39 73.1L567 601.1C576.4 610.5 591.6 610.5 600.9 601.1C610.2 591.7 610.3 576.5 600.9 567.2L449.8 416L480 416C490 416 499.5 411.3 505.5 403.3C511.5 395.3 513.5 384.9 510.7 375.2L507 361.8C494.6 318.5 466 283.3 428.8 262.1L418.5 128L448 128C465.7 128 480 113.7 480 96C480 78.3 465.7 64 448 64L192 64C184.6 64 177.9 66.5 172.5 70.6L222.1 120.3L217.3 183.4L73 39.1zM314.2 416L181.7 283.6C159 304.1 141.9 331 133 361.9L129.2 375.3C126.4 385 128.4 395.3 134.4 403.4C140.4 411.5 150 416 160 416L314.2 416zM288 576C288 593.7 302.3 608 320 608C337.7 608 352 593.7 352 576L352 464L288 464L288 576z" fill="currentColor"/></svg>`;
}

function getUnpinIconSvg(): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640"><path d="M160 96C160 78.3 174.3 64 192 64L448 64C465.7 64 480 78.3 480 96C480 113.7 465.7 128 448 128L418.5 128L428.8 262.1C465.9 283.3 494.6 318.5 507 361.8L510.8 375.2C513.6 384.9 511.6 395.2 505.6 403.3C499.6 411.4 490 416 480 416L160 416C150 416 140.5 411.3 134.5 403.3C128.5 395.3 126.5 384.9 129.3 375.2L133 361.8C145.4 318.5 174 283.3 211.2 262.1L221.5 128L192 128C174.3 128 160 113.7 160 96zM288 464L352 464L352 576C352 593.7 337.7 608 320 608C302.3 608 288 593.7 288 576L288 464z" fill="currentColor"/></svg>`;
}

export function getHeaderPinButtonHTML(config: Pick<WidgetConfig, 'headerPinned'>): string {
  const pinned = isHeaderPinned(config);
  const label = pinned ? 'Unpin header' : 'Pin header';
  return `<button type="button" class="header-pin-btn ${pinned ? 'is-pinned' : ''}" aria-label="${label}" title="${label}">${pinned ? getPinIconSvg() : getUnpinIconSvg()}</button>`;
}

export function applyHeaderPinState(
  shadowRoot: ShadowRoot,
  config: Pick<WidgetConfig, 'headerPinned'>,
  suppressReveal = false
): void {
  const widget = shadowRoot.querySelector('.widgetBox') as HTMLElement | null;
  const pinBtn = shadowRoot.querySelector('.header-pin-btn') as HTMLButtonElement | null;
  if (!widget) return;

  const pinned = isHeaderPinned(config);
  widget.classList.toggle('header-pinned', pinned);
  widget.classList.toggle('header-unpinned', !pinned);

  const host = shadowRoot.host;
  if (host instanceof HTMLElement) {
    host.setAttribute('data-header-pinned', String(pinned));
  }

  if (pinned) {
    widget.classList.remove('header-reveal-suppressed');
  } else if (suppressReveal) {
    widget.classList.add('header-reveal-suppressed');
    pinBtn?.blur();
  }

  if (pinBtn) {
    pinBtn.classList.toggle('is-pinned', pinned);
    const label = pinned ? 'Unpin header' : 'Pin header';
    pinBtn.innerHTML = pinned ? getPinIconSvg() : getUnpinIconSvg();
    pinBtn.setAttribute('aria-label', label);
    pinBtn.setAttribute('title', label);
  }
}

export function setupHeaderPin(shadowRoot: ShadowRoot, config: WidgetConfig): void {
  const widget = shadowRoot.querySelector('.widgetBox');
  const pinBtn = shadowRoot.querySelector('.header-pin-btn');
  widget?.addEventListener('mouseleave', () => {
    widget.classList.remove('header-reveal-suppressed');
  });
  pinBtn?.addEventListener('click', (event) => {
    event.stopPropagation();
    const wasPinned = isHeaderPinned(config);
    config.headerPinned = !wasPinned;
    applyHeaderPinState(shadowRoot, config, wasPinned);
    config.onHeaderPinChange?.(config.headerPinned);
  });
  applyHeaderPinState(shadowRoot, config);
}

/** JSON-serializable subset of candlestick widget config for export / embed previews (omits callbacks). */
export function pickCandlestickExportConfig(config: CandlestickChartConfig): Record<string, unknown> {
  const keys: (keyof CandlestickChartConfig)[] = [
    'symbol',
    'stock',
    'theme',
    'width',
    'height',
    'autosize',
    'title',
    'isSymbolSelectable',
    'period',
    'interval',
    'apiHost',
    'apiKey',
    'natsUrl',
    'natsToken',
    'natsStreamName',
    'natsTvId',
    'dataPoint',
    'chartTimezone',
    'p_min_yield',
    'p_top_n',
    'p_sort_by',
    'p_exclude_special_dividends',
    'start_date',
    'end_date',
  ];
  const out: Record<string, unknown> = {};
  const rawContainer = config.container;
  if (typeof rawContainer === 'string') {
    out.container = rawContainer;
  } else if (rawContainer && typeof (rawContainer as HTMLElement).id === 'string' && (rawContainer as HTMLElement).id) {
    out.container = `#${(rawContainer as HTMLElement).id}`;
  } else {
    out.container = '[HTMLElement]';
  }
  for (const k of keys) {
    const v = config[k];
    if (v !== undefined) {
      out[k as string] = v as unknown;
    }
  }
  out.hideSymbol = config.hideSymbol === true;
  out.hidePeriod = config.hidePeriod === true;
  out.hideInterval = config.hideInterval === true;
  return out;
}

/**
 * Chart request window on whole minutes (gateway cache, 15 Oct): `start_date` / `end_date` built from
 * `new Date()` at millisecond precision made every chart load a unique cache key. Facts compares
 * `timestamp >= start` and `timestamp <= end` and stamps a bar at the start of its period
 * (market-data-api #216), so flooring the start and ceiling the end to the minute never drops a bar.
 */
export function chartWindowStart(date: Date): string {
  const t = date.getTime();
  return new Date(Math.floor(t / 60_000) * 60_000).toISOString();
}
export function chartWindowEnd(date: Date): string {
  const t = date.getTime();
  return new Date(Math.ceil(t / 60_000) * 60_000).toISOString();
}
